import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  User,
  Clock,
  CheckCircle,
  Search,
  Phone,
  FileText,
  Plus,
  ChevronRight,
  MessageCircle,
  MapPin,
  Link,
  List,
  MousePointerClick,
  Settings,
  Loader2,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConversationsTab = ({ t }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/whatsapp/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (phone) => {
    try {
      const response = await axios.get(`${API_URL}/api/whatsapp/messages`, {
        params: { phone, limit: 50 }
      });
      setMessages(response.data.reverse());
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/send`, null, {
        params: {
          phone: selectedConversation._id,
          message: newMessage,
          patient_id: selectedConversation.patient_id
        }
      });
      setNewMessage('');
      fetchMessages(selectedConversation._id);
      toast.success(t('messages.sent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorSending'));
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[600px]">
      {/* Conversations List */}
      <Card className="lg:col-span-1 flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('conversations.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
            </div>
          ) : conversations.length > 0 ? (
            <div className="space-y-2">
              {conversations.map((conv, idx) => (
                <div
                  key={conv._id || conv.id || `conv-${idx}`}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedConversation?._id === conv._id 
                      ? 'bg-[#FDE8EA] border border-[#E63946]' 
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <MessageSquare className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {conv.patient_name || conv._id}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-1">
                          {conv.last_message}
                        </p>
                      </div>
                    </div>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-[#E63946]">{conv.unread_count}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(conv.last_timestamp).toLocaleString(undefined, {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>{t('conversations.noConversations')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Window */}
      <Card className="lg:col-span-2 flex flex-col">
        {selectedConversation ? (
          <>
            <CardHeader className="pb-2 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedConversation.patient_name || t('conversations.unknown')}</p>
                    <p className="text-sm text-gray-500">
                      <Phone className="h-3 w-3 inline mr-1" />
                      {selectedConversation._id}
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, idx) => (
                <div
                  key={msg._id || msg.id || `msg-${idx}`}
                  className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] p-3 rounded-lg ${
                      msg.direction === 'outbound'
                        ? 'bg-[#E63946] text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <p className="text-sm">{msg.message}</p>
                    <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                      msg.direction === 'outbound' ? 'text-red-200' : 'text-gray-400'
                    }`}>
                      <span>
                        {new Date(msg.timestamp).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {msg.direction === 'outbound' && msg.status === 'sent' && (
                        <CheckCircle className="h-3 w-3" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {messages.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3" />
                  <p>{t('conversations.noMessages')}</p>
                </div>
              )}
            </CardContent>
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  placeholder={t('chat.placeholder')}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  disabled={sendingMessage}
                  data-testid="whatsapp-message-input"
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  data-testid="send-whatsapp-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 mx-auto mb-4" />
              <p>{t('conversations.selectConversation')}</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const NewMessageTab = ({ t }) => {
  const [patients, setPatients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    patient_id: '',
    phone: '',
    message: '',
    use_template: false,
    template_name: '',
    template_params: []
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [patientsRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/api/patients?limit=100`),
        axios.get(`${API_URL}/api/whatsapp/templates`)
      ]);
      setPatients(patientsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handlePatientSelect = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setFormData({
        ...formData,
        patient_id: patientId,
        phone: patient.phone || patient.mobile || ''
      });
    }
  };

  const handleSendMessage = async () => {
    if (!formData.phone || (!formData.message && !formData.template_name)) {
      toast.error(t('newMessage.fillRequired'));
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/send`, null, {
        params: {
          phone: formData.phone,
          message: formData.message,
          patient_id: formData.patient_id || undefined,
          template_name: formData.template_name || undefined,
          template_params: formData.template_params.length > 0 ? formData.template_params : undefined
        }
      });
      toast.success(t('messages.sentSuccess'));
      setFormData({
        patient_id: '',
        phone: '',
        message: '',
        use_template: false,
        template_name: '',
        template_params: []
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorSending'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('newMessage.title')}</CardTitle>
          <CardDescription>{t('newMessage.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('newMessage.selectPatient')}</Label>
            <Select 
              value={formData.patient_id}
              onValueChange={handlePatientSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('newMessage.selectPatientPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {patients.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.first_name} {p.last_name} - {p.phone || p.mobile || t('newMessage.noPhone')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t('newMessage.whatsappNumber')}</Label>
            <Input
              placeholder="3001234567"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              data-testid="new-whatsapp-phone-input"
            />
            <p className="text-xs text-gray-500 mt-1">{t('newMessage.phoneFormat')}</p>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!formData.use_template}
                onChange={() => setFormData({...formData, use_template: false, template_name: ''})}
              />
              <span className="text-sm">{t('newMessage.freeMessage')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={formData.use_template}
                onChange={() => setFormData({...formData, use_template: true})}
              />
              <span className="text-sm">{t('newMessage.useTemplate')}</span>
            </label>
          </div>

          {formData.use_template ? (
            <div>
              <Label>{t('newMessage.template')}</Label>
              <Select
                value={formData.template_name}
                onValueChange={(v) => {
                  const template = templates.find(t => t.name === v);
                  setFormData({
                    ...formData,
                    template_name: v,
                    template_params: template?.params?.map(() => '') || []
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('newMessage.selectTemplatePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(tmpl => (
                    <SelectItem key={tmpl.id} value={tmpl.name}>
                      {tmpl.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {formData.template_name && templates.find(tmpl => tmpl.name === formData.template_name)?.params?.map((param, idx) => (
                <div key={idx} className="mt-2">
                  <Label className="text-sm">{t('newMessage.parameter')}: {param}</Label>
                  <Input
                    value={formData.template_params[idx] || ''}
                    onChange={(e) => {
                      const newParams = [...formData.template_params];
                      newParams[idx] = e.target.value;
                      setFormData({...formData, template_params: newParams});
                    }}
                    placeholder={`${t('newMessage.valueFor')} ${param}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div>
              <Label>{t('newMessage.message')}</Label>
              <Textarea
                placeholder={t('newMessage.messagePlaceholder')}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={4}
                data-testid="new-whatsapp-message-textarea"
              />
            </div>
          )}

          <Button 
            className="w-full"
            onClick={handleSendMessage}
            disabled={sending}
            data-testid="send-new-whatsapp-btn"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {t('newMessage.send')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const TemplatesTab = ({ t }) => {
  const [templates, setTemplates] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    body: '',
    params: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/whatsapp/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await axios.post(`${API_URL}/api/whatsapp/templates`, null, {
        params: {
          name: newTemplate.name,
          description: newTemplate.description,
          body: newTemplate.body,
          params: newTemplate.params.split(',').map(p => p.trim()).filter(p => p)
        }
      });
      toast.success(t('templates.messages.created'));
      setShowNewForm(false);
      setNewTemplate({ name: '', description: '', body: '', params: '' });
      fetchTemplates();
    } catch (error) {
      toast.error(t('templates.messages.createError'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowNewForm(true)} data-testid="new-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('templates.newTemplate')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(template => (
          <Card key={template.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{template.description}</p>
                  <p className="text-xs text-gray-500 mb-2">{t('templates.idLabel')}: {template.name}</p>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    {template.body}
                  </p>
                  {template.params?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {template.params.map((param, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {`{{${idx + 1}}} = ${param}`}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{t('templates.noTemplates')}</p>
        </div>
      )}

      {/* New Template Dialog */}
      <Dialog open={showNewForm} onOpenChange={setShowNewForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('templates.dialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('templates.dialog.nameId')}</Label>
              <Input
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                placeholder={t('templates.dialog.namePlaceholder')}
              />
            </div>
            <div>
              <Label>{t('templates.dialog.description')}</Label>
              <Input
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                placeholder={t('templates.dialog.descriptionPlaceholder')}
              />
            </div>
            <div>
              <Label>{t('templates.dialog.messageBody')}</Label>
              <Textarea
                value={newTemplate.body}
                onChange={(e) => setNewTemplate({...newTemplate, body: e.target.value})}
                placeholder={t('templates.dialog.bodyPlaceholder')}
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">{t('templates.dialog.bodyHint')}</p>
            </div>
            <div>
              <Label>{t('templates.dialog.parameters')}</Label>
              <Input
                value={newTemplate.params}
                onChange={(e) => setNewTemplate({...newTemplate, params: e.target.value})}
                placeholder={t('templates.dialog.paramsPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewForm(false)}>{t('templates.dialog.cancel')}</Button>
            <Button onClick={handleCreateTemplate} data-testid="save-template-btn">{t('templates.dialog.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== ADVANCED MESSAGES TAB ====================

const AdvancedMessagesTab = ({ t }) => {
  const [messageType, setMessageType] = useState('location_request');
  const [phone, setPhone] = useState('');
  const [nickname, setNickname] = useState('');
  const [sending, setSending] = useState(false);
  
  // Form data for different message types
  const [formData, setFormData] = useState({
    // Location Request
    location_body: t('advanced.locationRequest.placeholder'),
    
    // CTA URL
    cta_body: '',
    cta_button_text: '',
    cta_url: '',
    cta_header: '',
    cta_footer: '',
    
    // List Message
    list_body: '',
    list_button_text: t('advanced.list.buttonPlaceholder'),
    list_header: '',
    list_footer: '',
    list_sections: [
      {
        title: 'Options',
        rows: [{ id: 'opt1', title: '', description: '' }]
      }
    ],
    
    // Reply Buttons
    buttons_body: '',
    buttons_header: '',
    buttons_footer: '',
    buttons: [{ id: 'btn1', title: '' }, { id: 'btn2', title: '' }],
    
    // Location Send
    send_lat: '',
    send_lng: '',
    send_name: '',
    send_address: ''
  });

  const messageTypes = [
    { value: 'location_request', label: t('advanced.types.locationRequest.label'), icon: MapPin, desc: t('advanced.types.locationRequest.desc') },
    { value: 'cta_url', label: t('advanced.types.ctaUrl.label'), icon: Link, desc: t('advanced.types.ctaUrl.desc') },
    { value: 'list', label: t('advanced.types.list.label'), icon: List, desc: t('advanced.types.list.desc') },
    { value: 'reply_buttons', label: t('advanced.types.replyButtons.label'), icon: MousePointerClick, desc: t('advanced.types.replyButtons.desc') },
    { value: 'location', label: t('advanced.types.location.label'), icon: MapPin, desc: t('advanced.types.location.desc') }
  ];

  const handleSend = async () => {
    if (!phone) {
      toast.error(t('advanced.common.enterPhone'));
      return;
    }

    setSending(true);
    try {
      let endpoint = '';
      let payload = { to: phone, nickname: nickname || undefined };

      switch (messageType) {
        case 'location_request':
          endpoint = '/api/whatsapp/send/location-request';
          payload.body = formData.location_body;
          break;
        case 'cta_url':
          endpoint = '/api/whatsapp/send/cta-url';
          payload = { ...payload, body: formData.cta_body, button_text: formData.cta_button_text, button_url: formData.cta_url, header: formData.cta_header || undefined, footer: formData.cta_footer || undefined };
          break;
        case 'list':
          endpoint = '/api/whatsapp/send/list';
          payload = { ...payload, body: formData.list_body, button_text: formData.list_button_text, sections: formData.list_sections, header: formData.list_header || undefined, footer: formData.list_footer || undefined };
          break;
        case 'reply_buttons':
          endpoint = '/api/whatsapp/send/reply-buttons';
          payload = { ...payload, body: formData.buttons_body, buttons: formData.buttons, header: formData.buttons_header || undefined, footer: formData.buttons_footer || undefined };
          break;
        case 'location':
          endpoint = '/api/whatsapp/send/location';
          payload = { ...payload, latitude: parseFloat(formData.send_lat), longitude: parseFloat(formData.send_lng), name: formData.send_name || undefined, address: formData.send_address || undefined };
          break;
        default:
          return;
      }

      await axios.post(`${API_URL}${endpoint}`, payload);
      toast.success(t('messages.sentSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorSending'));
    } finally {
      setSending(false);
    }
  };

  const addListRow = (sectionIndex) => {
    const newSections = [...formData.list_sections];
    const rowCount = newSections[sectionIndex].rows.length;
    newSections[sectionIndex].rows.push({ id: `opt${rowCount + 1}`, title: '', description: '' });
    setFormData({ ...formData, list_sections: newSections });
  };

  const updateListRow = (sectionIndex, rowIndex, field, value) => {
    const newSections = [...formData.list_sections];
    newSections[sectionIndex].rows[rowIndex][field] = value;
    setFormData({ ...formData, list_sections: newSections });
  };

  const removeListRow = (sectionIndex, rowIndex) => {
    if (formData.list_sections[sectionIndex].rows.length <= 1) return;
    const newSections = [...formData.list_sections];
    newSections[sectionIndex].rows.splice(rowIndex, 1);
    setFormData({ ...formData, list_sections: newSections });
  };

  const addButton = () => {
    if (formData.buttons.length >= 3) return;
    const btnCount = formData.buttons.length;
    setFormData({ ...formData, buttons: [...formData.buttons, { id: `btn${btnCount + 1}`, title: '' }] });
  };

  const updateButton = (index, field, value) => {
    const newButtons = [...formData.buttons];
    newButtons[index][field] = value;
    setFormData({ ...formData, buttons: newButtons });
  };

  const removeButton = (index) => {
    if (formData.buttons.length <= 1) return;
    const newButtons = [...formData.buttons];
    newButtons.splice(index, 1);
    setFormData({ ...formData, buttons: newButtons });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Message Type Selection */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="text-base">{t('advanced.title')}</CardTitle>
          <CardDescription>{t('advanced.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {messageTypes.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => setMessageType(type.value)}
                className={`w-full p-3 rounded-lg border text-left transition-colors ${
                  messageType === type.value
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${messageType === type.value ? 'bg-green-500 text-white' : 'bg-gray-100'}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs text-gray-500">{type.desc}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Message Form */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {messageTypes.find(tp => tp.value === messageType)?.icon && 
              React.createElement(messageTypes.find(tp => tp.value === messageType).icon, { className: "h-5 w-5 text-green-500" })}
            {messageTypes.find(tp => tp.value === messageType)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Common Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('advanced.common.whatsappNumber')}</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+573001234567"
                data-testid="advanced-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('advanced.common.nickname')}</Label>
              <Input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={t('advanced.common.nicknamePlaceholder')}
              />
            </div>
          </div>

          {/* Location Request Form */}
          {messageType === 'location_request' && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <div className="space-y-2">
                <Label>{t('advanced.locationRequest.messageLabel')}</Label>
                <Textarea
                  value={formData.location_body}
                  onChange={(e) => setFormData({ ...formData, location_body: e.target.value })}
                  placeholder={t('advanced.locationRequest.placeholder')}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* CTA URL Form */}
          {messageType === 'cta_url' && (
            <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
              <div className="space-y-2">
                <Label>{t('advanced.ctaUrl.header')}</Label>
                <Input
                  value={formData.cta_header}
                  onChange={(e) => setFormData({ ...formData, cta_header: e.target.value })}
                  placeholder={t('advanced.ctaUrl.headerPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('advanced.ctaUrl.message')}</Label>
                <Textarea
                  value={formData.cta_body}
                  onChange={(e) => setFormData({ ...formData, cta_body: e.target.value })}
                  placeholder={t('advanced.ctaUrl.messagePlaceholder')}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('advanced.ctaUrl.buttonText')}</Label>
                  <Input
                    value={formData.cta_button_text}
                    onChange={(e) => setFormData({ ...formData, cta_button_text: e.target.value })}
                    placeholder={t('advanced.ctaUrl.buttonPlaceholder')}
                    maxLength={20}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('advanced.ctaUrl.url')}</Label>
                  <Input
                    value={formData.cta_url}
                    onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('advanced.ctaUrl.footer')}</Label>
                <Input
                  value={formData.cta_footer}
                  onChange={(e) => setFormData({ ...formData, cta_footer: e.target.value })}
                  placeholder={t('advanced.ctaUrl.footerPlaceholder')}
                />
              </div>
            </div>
          )}

          {/* List Message Form */}
          {messageType === 'list' && (
            <div className="space-y-4 p-4 bg-orange-50 rounded-lg">
              <div className="space-y-2">
                <Label>{t('advanced.list.header')}</Label>
                <Input
                  value={formData.list_header}
                  onChange={(e) => setFormData({ ...formData, list_header: e.target.value })}
                  placeholder={t('advanced.list.headerPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('advanced.list.message')}</Label>
                <Textarea
                  value={formData.list_body}
                  onChange={(e) => setFormData({ ...formData, list_body: e.target.value })}
                  placeholder={t('advanced.list.messagePlaceholder')}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('advanced.list.buttonText')}</Label>
                <Input
                  value={formData.list_button_text}
                  onChange={(e) => setFormData({ ...formData, list_button_text: e.target.value })}
                  placeholder={t('advanced.list.buttonPlaceholder')}
                  maxLength={20}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t('advanced.list.optionsLabel')}</Label>
                  <Button variant="outline" size="sm" onClick={() => addListRow(0)}>
                    <Plus className="h-4 w-4 mr-1" /> {t('advanced.list.add')}
                  </Button>
                </div>
                {formData.list_sections[0].rows.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input
                        value={row.title}
                        onChange={(e) => updateListRow(0, rowIndex, 'title', e.target.value)}
                        placeholder={t('advanced.list.optionTitle')}
                        maxLength={24}
                      />
                      <Input
                        value={row.description}
                        onChange={(e) => updateListRow(0, rowIndex, 'description', e.target.value)}
                        placeholder={t('advanced.list.optionDescription')}
                        maxLength={72}
                      />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeListRow(0, rowIndex)} disabled={formData.list_sections[0].rows.length <= 1}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reply Buttons Form */}
          {messageType === 'reply_buttons' && (
            <div className="space-y-4 p-4 bg-green-50 rounded-lg">
              <div className="space-y-2">
                <Label>{t('advanced.replyButtons.header')}</Label>
                <Input
                  value={formData.buttons_header}
                  onChange={(e) => setFormData({ ...formData, buttons_header: e.target.value })}
                  placeholder={t('advanced.replyButtons.headerPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('advanced.replyButtons.message')}</Label>
                <Textarea
                  value={formData.buttons_body}
                  onChange={(e) => setFormData({ ...formData, buttons_body: e.target.value })}
                  placeholder={t('advanced.replyButtons.messagePlaceholder')}
                  rows={2}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t('advanced.replyButtons.buttonsLabel')}</Label>
                  <Button variant="outline" size="sm" onClick={addButton} disabled={formData.buttons.length >= 3}>
                    <Plus className="h-4 w-4 mr-1" /> {t('advanced.replyButtons.add')}
                  </Button>
                </div>
                {formData.buttons.map((btn, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      value={btn.title}
                      onChange={(e) => updateButton(index, 'title', e.target.value)}
                      placeholder={`${t('advanced.replyButtons.buttonPlaceholder')} ${index + 1}`}
                      maxLength={20}
                      className="flex-1"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeButton(index)} disabled={formData.buttons.length <= 1}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Send Location Form */}
          {messageType === 'location' && (
            <div className="space-y-4 p-4 bg-teal-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('advanced.sendLocation.latitude')}</Label>
                  <Input
                    value={formData.send_lat}
                    onChange={(e) => setFormData({ ...formData, send_lat: e.target.value })}
                    placeholder="4.7110"
                    type="number"
                    step="any"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('advanced.sendLocation.longitude')}</Label>
                  <Input
                    value={formData.send_lng}
                    onChange={(e) => setFormData({ ...formData, send_lng: e.target.value })}
                    placeholder="-74.0721"
                    type="number"
                    step="any"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('advanced.sendLocation.placeName')}</Label>
                <Input
                  value={formData.send_name}
                  onChange={(e) => setFormData({ ...formData, send_name: e.target.value })}
                  placeholder={t('advanced.sendLocation.namePlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('advanced.sendLocation.address')}</Label>
                <Input
                  value={formData.send_address}
                  onChange={(e) => setFormData({ ...formData, send_address: e.target.value })}
                  placeholder={t('advanced.sendLocation.addressPlaceholder')}
                />
              </div>
            </div>
          )}

          <Button 
            onClick={handleSend} 
            className="w-full bg-green-500 hover:bg-green-600"
            disabled={sending}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            {t('advanced.common.send')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const WhatsAppPage = () => {
  const { t } = useTranslation('whatsapp');
  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
        <p className="text-gray-500 mt-1">{t('subtitle')}</p>
      </div>

      {/* Status Card */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-green-800">{t('statusCard.apiTitle')}</p>
              <p className="text-sm text-green-600">
                {t('statusCard.configureHint')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="conversations" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="conversations" data-testid="conversations-tab">
            <MessageCircle className="h-4 w-4 mr-2" />
            {t('tabs.conversations')}
          </TabsTrigger>
          <TabsTrigger value="new" data-testid="new-message-tab">
            <Send className="h-4 w-4 mr-2" />
            {t('tabs.newMessage')}
          </TabsTrigger>
          <TabsTrigger value="advanced" data-testid="advanced-tab">
            <Settings className="h-4 w-4 mr-2" />
            {t('tabs.advanced')}
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">
            <FileText className="h-4 w-4 mr-2" />
            {t('tabs.templates')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conversations">
          <ConversationsTab t={t} />
        </TabsContent>

        <TabsContent value="new">
          <NewMessageTab t={t} />
        </TabsContent>

        <TabsContent value="advanced">
          <AdvancedMessagesTab t={t} />
        </TabsContent>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>{t('templates.title')}</CardTitle>
              <CardDescription>{t('templates.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <TemplatesTab t={t} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WhatsAppPage;
